// =========================================================================
// Animora Achievements - catalog + pure helpers.
//
// Achievements are 100% FREE (never behind Premium). Everything about them is
// defined right here: to add a new one, append an entry to ACHIEVEMENTS.
//   - `stat`   -> which tracked number it depends on (see AchievementStats)
//   - `target` -> value that stat must reach
//   - `xp`     -> XP awarded on unlock (level is derived from total XP)
// Nothing in here touches Firebase, so it is safe to import anywhere.
// =========================================================================

export type AchievementCategoryId = 'activity' | 'social' | 'creator' | 'chat' | 'gifts'

export type AchievementStats = {
  posts: number // all posts the user published (reels included)
  reels: number // video posts
  stories: number // stories ever published
  reelViews: number // total views across all reels
  likes: number // total likes across all own posts
  friends: number
  followers: number
  groups: number // group chats the user is a member of
  messages: number // total messages sent
  groupMessages: number // messages sent inside group chats
  giftsSent: number
  giftsReceived: number
  streak: number // best daily-activity streak
}

export type AchievementStatKey = keyof AchievementStats

export type AchievementDef = {
  id: string
  category: AchievementCategoryId
  title: string
  description: string // "what you need to do" (shown while locked)
  unlockText: string // shown in the unlock popup: "You reached ..."
  icon: string // emoji
  stat: AchievementStatKey
  target: number
  xp: number
  // Locked mystery achievements hide their real name behind a teaser
  // ("🔒 ???  Mystery Creator") until they are unlocked.
  mysteryTitle?: string
}

export const ACHIEVEMENT_CATEGORIES: { id: AchievementCategoryId; label: string; emoji: string }[] = [
  { id: 'activity', label: 'Activity', emoji: '🔥' },
  { id: 'social', label: 'Social', emoji: '👥' },
  { id: 'creator', label: 'Creator', emoji: '🎬' },
  { id: 'chat', label: 'Chat', emoji: '💬' },
  { id: 'gifts', label: 'Gifts', emoji: '🎁' },
]

export const ACHIEVEMENTS: AchievementDef[] = [
  // ---- 🔥 Activity ----
  { id: 'first_post', category: 'activity', title: 'First Post', description: 'Create your first post', unlockText: 'You published your first post', icon: '📝', stat: 'posts', target: 1, xp: 25 },
  { id: 'first_reel', category: 'activity', title: 'First Reel', description: 'Upload your first reel', unlockText: 'You uploaded your first reel', icon: '🎞️', stat: 'reels', target: 1, xp: 25 },
  { id: 'first_story', category: 'activity', title: 'First Story', description: 'Publish your first story', unlockText: 'You published your first story', icon: '📸', stat: 'stories', target: 1, xp: 25 },
  { id: 'streak_7', category: 'activity', title: '7 Day Streak', description: 'Be active 7 days in a row', unlockText: 'You stayed active 7 days in a row', icon: '🔥', stat: 'streak', target: 7, xp: 75 },
  { id: 'streak_30', category: 'activity', title: 'Unstoppable', mysteryTitle: 'Mystery Streak', description: 'Be active 30 days in a row', unlockText: 'You stayed active 30 days in a row', icon: '⚡', stat: 'streak', target: 30, xp: 250 },

  // ---- 👥 Social ----
  { id: 'first_friend', category: 'social', title: 'First Friend', description: 'Make your first friend', unlockText: 'You made your first friend', icon: '🤝', stat: 'friends', target: 1, xp: 25 },
  { id: 'friends_10', category: 'social', title: '10 Friends', description: 'Have 10 friends', unlockText: 'You now have 10 friends', icon: '👫', stat: 'friends', target: 10, xp: 75 },
  { id: 'followers_100', category: 'social', title: '100 Followers', description: 'Reach 100 followers', unlockText: 'You reached 100 followers', icon: '🌟', stat: 'followers', target: 100, xp: 150 },
  { id: 'followers_1k', category: 'social', title: 'Community Icon', mysteryTitle: 'Mystery Star', description: 'Reach 1,000 followers', unlockText: 'You reached 1,000 followers', icon: '👑', stat: 'followers', target: 1000, xp: 400 },
  { id: 'first_group', category: 'social', title: 'First Group Joined', description: 'Join or create your first group', unlockText: 'You joined your first group', icon: '🫂', stat: 'groups', target: 1, xp: 25 },

  // ---- 🎬 Creator ----
  { id: 'reels_10', category: 'creator', title: '10 Reels', description: 'Upload 10 reels', unlockText: 'You uploaded 10 reels', icon: '🎬', stat: 'reels', target: 10, xp: 75 },
  { id: 'reels_100', category: 'creator', title: '100 Reels', description: 'Upload 100 reels', unlockText: 'You uploaded 100 reels', icon: '🎥', stat: 'reels', target: 100, xp: 300 },
  { id: 'views_1k', category: 'creator', title: 'Rising Creator', description: 'Reach 1,000 total reel views', unlockText: 'You reached 1,000 reel views', icon: '🥇', stat: 'reelViews', target: 1000, xp: 100 },
  { id: 'views_10k', category: 'creator', title: 'Viral Spark', mysteryTitle: 'Mystery Creator', description: 'Reach 10,000 reel views', unlockText: 'You reached 10,000 reel views', icon: '🚀', stat: 'reelViews', target: 10000, xp: 250 },
  { id: 'views_100k', category: 'creator', title: 'Reel Legend', mysteryTitle: 'Mystery Legend', description: 'Reach 100,000 reel views', unlockText: 'You reached 100,000 reel views', icon: '🏆', stat: 'reelViews', target: 100000, xp: 500 },
  { id: 'likes_1k', category: 'creator', title: '1K Likes', description: 'Collect 1,000 likes on your posts', unlockText: 'You collected 1,000 likes', icon: '❤️', stat: 'likes', target: 1000, xp: 150 },

  // ---- 💬 Chat ----
  { id: 'first_message', category: 'chat', title: 'First Message', description: 'Send your first message', unlockText: 'You sent your first message', icon: '💬', stat: 'messages', target: 1, xp: 25 },
  { id: 'messages_100', category: 'chat', title: '100 Messages', description: 'Send 100 messages', unlockText: 'You sent 100 messages', icon: '✉️', stat: 'messages', target: 100, xp: 75 },
  { id: 'messages_1k', category: 'chat', title: '1K Messages', description: 'Send 1,000 messages', unlockText: 'You sent 1,000 messages', icon: '🗨️', stat: 'messages', target: 1000, xp: 200 },
  { id: 'first_group_chat', category: 'chat', title: 'First Group Chat', description: 'Send a message in a group chat', unlockText: 'You sent your first group message', icon: '👥', stat: 'groupMessages', target: 1, xp: 25 },

  // ---- 🎁 Gifts ----
  { id: 'gift_sent_1', category: 'gifts', title: 'Send First Gift', description: 'Send your first gift', unlockText: 'You sent your first gift', icon: '🎁', stat: 'giftsSent', target: 1, xp: 50 },
  { id: 'gift_recv_1', category: 'gifts', title: 'Receive First Gift', description: 'Receive your first gift', unlockText: 'You received your first gift', icon: '💝', stat: 'giftsReceived', target: 1, xp: 50 },
  { id: 'gift_recv_10', category: 'gifts', title: '10 Gifts Received', description: 'Receive 10 gifts', unlockText: 'You received 10 gifts', icon: '🎀', stat: 'giftsReceived', target: 10, xp: 150 },
  { id: 'gift_recv_100', category: 'gifts', title: 'Gift Magnet', mysteryTitle: 'Mystery Fan Favorite', description: 'Receive 100 gifts', unlockText: 'You received 100 gifts', icon: '💎', stat: 'giftsReceived', target: 100, xp: 400 },
]

export const EMPTY_ACHIEVEMENT_STATS: AchievementStats = {
  posts: 0, reels: 0, stories: 0, reelViews: 0, likes: 0,
  friends: 0, followers: 0, groups: 0,
  messages: 0, groupMessages: 0, giftsSent: 0, giftsReceived: 0, streak: 0,
}

export type AchievementUnlockMap = Record<string, number> // id -> unlocked-at (ms epoch)

export type AchievementProgress = { value: number; target: number; pct: number; done: boolean }

export function getProgress(def: AchievementDef, stats: AchievementStats): AchievementProgress {
  const value = Math.max(0, Math.min(stats[def.stat] || 0, def.target))
  const pct = Math.floor((value / def.target) * 100)
  return { value, target: def.target, pct, done: value >= def.target }
}

export function getAchievement(id: string): AchievementDef | undefined {
  return ACHIEVEMENTS.find((a) => a.id === id)
}

export function totalXp(unlocked: AchievementUnlockMap | undefined): number {
  if (!unlocked) return 0
  return ACHIEVEMENTS.reduce((sum, a) => (unlocked[a.id] ? sum + a.xp : sum), 0)
}

// Level curve: 50 XP -> Lv2, 200 -> Lv3, 450 -> Lv4, 800 -> Lv5 ... (level = floor(sqrt(xp/50)) + 1)
export function levelInfo(xp: number) {
  const level = Math.floor(Math.sqrt(xp / 50)) + 1
  const floorXp = 50 * (level - 1) * (level - 1)
  const nextXp = 50 * level * level
  return {
    level,
    xp,
    floorXp,
    nextXp,
    pct: Math.min(100, Math.floor(((xp - floorXp) / (nextXp - floorXp)) * 100)),
  }
}

export function unlockedCount(unlocked: AchievementUnlockMap | undefined): number {
  if (!unlocked) return 0
  return ACHIEVEMENTS.filter((a) => unlocked[a.id]).length
}

// Achievements whose goal is met but that were never recorded as unlocked yet.
export function findNewlyCompleted(stats: AchievementStats, unlocked: AchievementUnlockMap): AchievementDef[] {
  return ACHIEVEMENTS.filter((a) => !unlocked[a.id] && getProgress(a, stats).done)
}

// Achievement shown in the big "Featured" card: the most recently unlocked one,
// or - if nothing is unlocked yet - the locked one that is closest to completion.
export function pickFeatured(stats: AchievementStats, unlocked: AchievementUnlockMap): AchievementDef | undefined {
  const done = ACHIEVEMENTS.filter((a) => unlocked[a.id]).sort((a, b) => unlocked[b.id] - unlocked[a.id])
  if (done.length) return done[0]
  return [...ACHIEVEMENTS].sort((a, b) => getProgress(b, stats).pct - getProgress(a, stats).pct)[0]
}

// Most valuable unlocked achievements first - used for the small profile badges.
export function topUnlocked(unlocked: AchievementUnlockMap | undefined, limit: number): AchievementDef[] {
  if (!unlocked) return []
  return ACHIEVEMENTS.filter((a) => unlocked[a.id])
    .sort((a, b) => b.xp - a.xp || unlocked[b.id] - unlocked[a.id])
    .slice(0, limit)
}

export function formatNum(n: number): string {
  return n.toLocaleString('en-US')
}

// Local calendar day key ("2026-09-19"); offset -1 = yesterday. Used for streaks.
export function localDayKey(offsetDays = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}
